/**
 * Bulk note actions — plain functions (not hooks) that wrap existing db primitives
 * in a single transaction for performance. Each note still gets its own sync op.
 */

import {
  archiveNote,
  deleteNoteLocal,
  enqueueSyncOp,
  getNote,
  softDeleteNote,
  leaveSharedNote,
  restoreNote,
  unarchiveNote,
  updateNote,
} from './db'
import { execute, notifyChanges, transaction } from './sqlite'
import type { ExportFormat } from './export'
import { downloadBlob, sanitizeFilename } from './export/download'
import { track, AnalyticsEvent } from './analytics'
import { useUIStore } from '../store/ui'
import { isSharedWithMe } from './sharing'
import { memberTextLocked } from './noteEngineState'

export function bulkPin(ids: string[], isPinned: boolean): void {
  transaction(() => {
    for (const id of ids) {
      // Capture updatedAt BEFORE updateNote bumps it, so the sync op carries
      // the pre-update value for optimistic concurrency.
      const expectedUpdatedAt = getNote(id)?.updatedAt
      updateNote(id, { isPinned, syncStatus: 'pending' })
      enqueueSyncOp({
        type: 'update',
        entity: 'note',
        entityId: id,
        data: { is_pinned: isPinned, expected_updated_at: expectedUpdatedAt },
        timestamp: Date.now(),
        retries: 0,
      })
    }
  })
  notifyChanges(['notes', 'sync_queue'])
  track(AnalyticsEvent.NoteAction, {
    action: isPinned ? 'pin' : 'unpin',
    source: 'bulk',
    count: ids.length,
  })
}

/** `me`: the signed-in account, whose notes are its own (a member's delete is Leave). */
export function bulkDelete(ids: string[], me?: string | null): void {
  let left = 0
  transaction(() => {
    for (const id of ids) {
      // A member's delete means Leave (PLAN.md D5).
      if (isSharedWithMe(getNote(id), me)) {
        leaveSharedNote(id)
        left++
        continue
      }
      softDeleteNote(id)
      enqueueSyncOp({
        type: 'soft-delete',
        entity: 'note',
        entityId: id,
        timestamp: Date.now(),
        retries: 0,
      })
    }
  })
  notifyChanges(['notes', 'note_tags', 'tags', 'sync_queue'])
  if (left > 0) track(AnalyticsEvent.SharedNoteLeft, { source: 'bulk' })
  if (ids.length > left) track(AnalyticsEvent.NoteAction, { action: 'trash', source: 'bulk', count: ids.length - left })
}

export function bulkArchive(ids: string[]): void {
  transaction(() => {
    for (const id of ids) {
      const expectedUpdatedAt = getNote(id)?.updatedAt
      archiveNote(id)
      const archivedAt = getNote(id)?.archivedAt ?? new Date().toISOString()
      // Archive rides the generic 'update' op (mirrors pin) — no new op type.
      enqueueSyncOp({
        type: 'update',
        entity: 'note',
        entityId: id,
        data: { archived_at: archivedAt, expected_updated_at: expectedUpdatedAt },
        timestamp: Date.now(),
        retries: 0,
      })
    }
  })
  notifyChanges(['notes', 'sync_queue'])
  track(AnalyticsEvent.NoteAction, { action: 'archive', source: 'bulk', count: ids.length })
}

export function bulkUnarchive(ids: string[]): void {
  transaction(() => {
    for (const id of ids) {
      const expectedUpdatedAt = getNote(id)?.updatedAt
      unarchiveNote(id)
      enqueueSyncOp({
        type: 'update',
        entity: 'note',
        entityId: id,
        data: { archived_at: null, expected_updated_at: expectedUpdatedAt },
        timestamp: Date.now(),
        retries: 0,
      })
    }
  })
  notifyChanges(['notes', 'sync_queue'])
  track(AnalyticsEvent.NoteAction, { action: 'unarchive', source: 'bulk', count: ids.length })
}

export function bulkRestore(ids: string[]): void {
  transaction(() => {
    for (const id of ids) {
      restoreNote(id)
      enqueueSyncOp({
        type: 'restore',
        entity: 'note',
        entityId: id,
        timestamp: Date.now(),
        retries: 0,
      })
    }
  })
  notifyChanges(['notes', 'sync_queue'])
  track(AnalyticsEvent.NoteAction, { action: 'restore', source: 'bulk', count: ids.length })
}

export function bulkDeletePermanently(ids: string[]): void {
  transaction(() => {
    for (const id of ids) {
      deleteNoteLocal(id)
      enqueueSyncOp({
        type: 'delete',
        entity: 'note',
        entityId: id,
        timestamp: Date.now(),
        retries: 0,
      })
    }
  })
  notifyChanges(['notes', 'note_tags', 'note_links', 'sync_queue'])
  track(AnalyticsEvent.NoteAction, {
    action: 'delete_permanent',
    source: 'bulk',
    count: ids.length,
  })
}

/** Notes this account may edit: a viewer's note is left alone (PLAN.md D5), any member's in legacy mode. */
function editableNoteIds(noteIds: string[]): string[] {
  return noteIds.filter((id) => !memberTextLocked(getNote(id)?.role))
}

export function bulkAddTag(noteIds: string[], tagId: string): void {
  transaction(() => {
    for (const noteId of editableNoteIds(noteIds)) {
      execute(
        'INSERT OR IGNORE INTO note_tags (note_id, tag_id) VALUES (?, ?)',
        [noteId, tagId],
      )
      enqueueSyncOp({
        type: 'update',
        entity: 'note',
        entityId: noteId,
        data: { add_tag: tagId },
        timestamp: Date.now(),
        retries: 0,
      })
    }
  })
  notifyChanges(['note_tags', 'sync_queue'])
}

export function bulkRemoveTag(noteIds: string[], tagId: string): void {
  transaction(() => {
    for (const noteId of editableNoteIds(noteIds)) {
      execute('DELETE FROM note_tags WHERE note_id = ? AND tag_id = ?', [
        noteId,
        tagId,
      ])
      enqueueSyncOp({
        type: 'update',
        entity: 'note',
        entityId: noteId,
        data: { remove_tag: tagId },
        timestamp: Date.now(),
        retries: 0,
      })
    }
  })
  notifyChanges(['note_tags', 'sync_queue'])
}

/**
 * Yield one macrotask so the browser can paint. Deliberately a MessageChannel
 * and not `setTimeout`: chained timers are clamped to >= 1s in a hidden tab (and
 * to one wake per minute under intensive throttling), which would stretch a
 * backgrounded bulk export from seconds into hours. Port messages aren't a timer
 * source, so they're exempt — this is what React's own scheduler uses.
 */
function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => {
    const channel = new MessageChannel()
    channel.port1.onmessage = () => {
      channel.port1.close()
      resolve()
    }
    channel.port2.postMessage(null)
  })
}

export async function bulkExport(
  noteIds: string[],
  format: ExportFormat,
): Promise<void> {
  const { exportNote, getExportBlob } = await import('./export')

  // Single note — use the standard per-note download
  if (noteIds.length === 1) {
    await exportNote(noteIds[0], format)
    return
  }

  // Multiple notes — generate each blob with the same code path, then ZIP.
  // The blocking progress dialog is up for the whole loop: closing the tab
  // mid-run loses the export, and a big library takes a while.
  const { setExportProgress } = useUIStore.getState()
  setExportProgress({ done: 0, total: noteIds.length })
  try {
    const JSZip = (await import('jszip')).default
    const zip = new JSZip()

    for (const [i, id] of noteIds.entries()) {
      const note = getNote(id)
      if (note) {
        const { blob, ext } = await getExportBlob(note.content, note.title, format)
        const filename = `${sanitizeFilename(note.title || 'Untitled')}.${ext}`
        zip.file(filename, blob)
      }
      setExportProgress({ done: i + 1, total: noteIds.length })
      // The awaits above are microtasks for most formats, so without a macrotask
      // hop the browser never repaints and the counter freezes.
      await yieldToBrowser()
    }

    const blob = await zip.generateAsync({ type: 'blob' })
    downloadBlob(blob, `caliu-export-${noteIds.length}-notes.zip`)
  } finally {
    setExportProgress(null)
  }
  // Single-note exports fire from exportNote; only the bulk ZIP path fires here.
  track(AnalyticsEvent.NoteExported, {
    format,
    scope: 'bulk',
    count: noteIds.length,
  })
}
